// src/utils/usersMe.ts
//
// Best-effort client for `GET /api/v1/ui/users/me` (Decision 8 of the
// hotel-operations redesign): `{ role, capabilities: ["technical"], hotelIds }`.
// The API lane may still be building this endpoint, so every caller must
// treat a failure (network error, 404, non-2xx) as "keep whatever the token
// already gave us" — never block sign-in on this call.
import { uiApiUrl } from '@/utils/uiApiUrl';
import keycloak from '@/lib/keycloak';
import type { UserRole } from '@/types/models';

export interface UsersMeResponse {
  role: UserRole;
  capabilities: string[];
  hotelIds: string[];
  /** True for a person who holds only a documentation role: the Developers section and nothing else. */
  documentationOnly: boolean;
}

/** Envelope used across the API: `{ success, data }`. */
interface Envelope<T> {
  success?: boolean;
  data?: T;
}

/**
 * Fetches the authoritative role/capabilities/scope for the current user.
 * Returns `null` when the endpoint is unavailable or the response is not the
 * expected shape — callers must fall back to the token-derived values.
 */
export const fetchUsersMe = async (): Promise<UsersMeResponse | null> => {
  const token = keycloak.token;
  if (!token) return null;

  try {
    const response = await fetch(uiApiUrl('users/me'), {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) return null;

    const body = (await response.json()) as Envelope<UsersMeResponse> | UsersMeResponse;
    const data = (body as Envelope<UsersMeResponse>).data ?? (body as UsersMeResponse);

    if (!data || typeof data.role !== 'string') return null;

    return {
      role: data.role,
      capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
      hotelIds: Array.isArray(data.hotelIds) ? data.hotelIds : [],
      documentationOnly: data.documentationOnly === true,
    };
  } catch {
    return null;
  }
};
